import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext(null);

// The reset email links here as ?token_hash=…&type=recovery (the Supabase
// "Reset password" template sends this app's resets back to RESET_REDIRECT).
// The link carries no session: verifyOtp below trades it for one. Only this
// page's JS spends it, so a mail scanner that prefetches the link can't.
const RECOVERY_TOKEN_HASH = (() => {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  return q.get('type') === 'recovery' ? q.get('token_hash') : null;
})();

// Older links deliver the recovery session in the URL hash (#...&type=recovery)
// and Supabase emits PASSWORD_RECOVERY only after it asynchronously processes it.
// We read the URL synchronously at module load — before Supabase strips it — so
// the UI can start in recovery mode on the very first render, with no flash of
// the app.
const INITIAL_RECOVERY =
  Boolean(RECOVERY_TOKEN_HASH) ||
  (typeof window !== 'undefined' && /[#&]type=recovery(&|$)/.test(window.location.hash || ''));

// Where a reset email returns the user. The template matches this exact URL,
// so changing it sends this app's resets to the marketplace reset page instead.
const RESET_REDIRECT =
  typeof window !== 'undefined' ? `${window.location.origin}/engine/` : '';

// Mirrored into localStorage for two consumers that still read them directly:
//   - the axios interceptor below (sync access to the current access token)
//   - the scraper handoff, which reads the stored profile to send alongside the
//     session. The session itself comes from getSession() and travels in a
//     cookie, never in the URL — see utils/sessionHandoff.
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

const setStoredToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};
const setStoredUser = (u) => {
  if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_KEY);
};

// Every API call carries the current Supabase access token as a Bearer header.
// The backend verifies it against the shared Supabase project's JWKS.
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  // True after the user follows a Supabase password-recovery email link.
  // Seeded synchronously from the URL so the reset form shows immediately.
  const [recoveryMode, setRecoveryMode] = useState(INITIAL_RECOVERY);
  // True when a ?token_hash= reset link could not be verified.
  const [recoveryFailed, setRecoveryFailed] = useState(false);

  // Pull the local profile (role + approval) from our backend. Returns the
  // profile, or null if the account is pending approval / unavailable.
  const loadProfile = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/auth/me');
      if (data?.pending_approval) {
        setPendingApproval(true);
        setUser(null);
        setStoredUser(null);
        return null;
      }
      setPendingApproval(false);
      setUser(data);
      setStoredUser(data);
      return data;
    } catch {
      setUser(null);
      setStoredUser(null);
      return null;
    }
  }, []);

  // The full Supabase session, kept in a ref so callers can reach it without a
  // re-render and without an await. The scraper handoff needs the refresh token
  // that lives on it, and a click handler cannot wait for getSession().
  const sessionRef = useRef(null);

  const applySession = useCallback((session) => {
    sessionRef.current = session || null;
    const t = session?.access_token || null;
    setToken(t);
    setStoredToken(t);
    return t;
  }, []);

  const getCurrentSession = useCallback(() => sessionRef.current, []);

  // Hydrate from any existing Supabase session, then keep token in sync as
  // Supabase refreshes it / signs in / signs out.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const t = applySession(data?.session);
      if (t) loadProfile();
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
      const t = applySession(session);
      if (t) {
        // Don't re-fetch the profile on every token refresh during recovery.
        if (event !== 'TOKEN_REFRESHED') loadProfile();
      } else {
        setUser(null);
        setStoredUser(null);
      }
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [applySession, loadProfile]);

  // Trade a ?token_hash= reset link for a recovery session. Success arrives as
  // PASSWORD_RECOVERY above; a spent or expired link sends the user back to
  // request a new one.
  const recoveryLinkUsed = useRef(false);
  useEffect(() => {
    if (!RECOVERY_TOKEN_HASH || recoveryLinkUsed.current) return;
    recoveryLinkUsed.current = true;
    // Single use: drop it from the address bar so a reload doesn't retry it.
    const url = new URL(window.location.href);
    url.searchParams.delete('token_hash');
    url.searchParams.delete('type');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    supabase.auth.verifyOtp({ token_hash: RECOVERY_TOKEN_HASH, type: 'recovery' })
      .then(({ error: err }) => {
        if (!err) return;
        setRecoveryMode(false);
        setRecoveryFailed(true);
        setError('This reset link has expired or was already used. Enter your email to get a new one.');
      });
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError('');
    setPendingApproval(false);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        setError(
          err.message?.toLowerCase().includes('email not confirmed')
            ? 'Please confirm your email address first (check your inbox).'
            : 'Invalid email or password',
        );
        return false;
      }
      const profile = await loadProfile();
      if (!profile) {
        // Authenticated with Supabase but not approved in Auction.ai yet.
        await supabase.auth.signOut();
        setError('Your account is awaiting administrator approval.');
        return false;
      }
      return true;
    } finally {
      setLoading(false);
    }
  }, [loadProfile]);

  const signup = useCallback(async ({ email, username, password }) => {
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { username } },
      });
      if (err) {
        const msg = err.message || '';
        if (/already registered|already exists/i.test(msg)) {
          setError('An account with that email already exists.');
        } else if (/password/i.test(msg)) {
          setError('Password is too weak.');
        } else {
          setError('Sign-up failed. Please try again.');
        }
        return false;
      }
      // New accounts need (a) email confirmation if enabled in Supabase, and
      // (b) superuser approval in Auction.ai. Either way: don't auto-enter.
      await supabase.auth.signOut();
      return { ok: true, pendingApproval: true };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sends a Supabase password-reset email. The link returns the user to the
  // app with a recovery session (see PASSWORD_RECOVERY above), where they set a
  // new password via `updatePassword`.
  const requestPasswordReset = useCallback(async (email) => {
    setLoading(true);
    setError('');
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: RESET_REDIRECT,
      });
      if (err) {
        setError('Could not start password reset. Please try again.');
        return false;
      }
      return { ok: true };
    } finally {
      setLoading(false);
    }
  }, []);

  // Set a new password. Works both in recovery mode (after the email link) and
  // for a normal signed-in user changing their password.
  const updatePassword = useCallback(async ({ password }) => {
    setError('');
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        return { ok: false, error: err.message || 'Could not update password.' };
      }
      setRecoveryMode(false);
      return { ok: true };
    } catch {
      return { ok: false, error: 'Could not update password.' };
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setError('');
    try {
      await axios.delete('/api/auth/account');
    } catch (e) {
      const code = e?.response?.data?.error;
      if (code === 'cannot_delete_superuser') {
        return {
          ok: false,
          error: e?.response?.data?.message
            || 'Superuser accounts must be removed by another superuser.',
        };
      }
      return { ok: false, error: 'Could not delete account.' };
    }
    await supabase.auth.signOut();
    return { ok: true };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setToken(null);
    setUser(null);
    setPendingApproval(false);
    setStoredToken(null);
    setStoredUser(null);
  }, []);

  const value = useMemo(() => ({
    token,
    user,
    role: user?.role || null,
    getCurrentSession,
    isAuthenticated: !!token && !!user,
    pendingApproval,
    recoveryMode,
    recoveryFailed,
    loading,
    error,
    login,
    signup,
    requestPasswordReset,
    updatePassword,
    deleteAccount,
    logout,
  }), [token, user, pendingApproval, recoveryMode, recoveryFailed, loading, error, getCurrentSession,
       login, signup, requestPasswordReset, updatePassword, deleteAccount, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const hasRole = (role, ...allowed) => {
  if (!role) return false;
  if (role === 'superuser') return true;
  return allowed.includes(role);
};
